import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, Select, Form, Alert, message, Space, Tag } from 'antd';
import { InboxOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { importSerial } from '../../api/serial';
import { getModelList, getPlatformList } from '../../api/config';
import { downloadCsv } from '../../utils/exportCsv';

const { Dragger } = Upload;

const PLATFORM_OPTIONS = ['电信', '移动', '联通', '涂鸦'].map((v) => ({ value: v, label: v }));

const SerialImport = () => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const [modelOptions, setModelOptions] = useState([]);
    const [platformFieldMap, setPlatformFieldMap] = useState({});
    const watchPlatform = Form.useWatch('platform', form);
    const watchModel = Form.useWatch('model', form);
    const selectedMacStep = modelOptions.find((m) => m.value === watchModel)?.mac_step;

    useEffect(() => {
        const fetchPlatforms = async () => {
            const res = await getPlatformList();
            if (res.code === 0) {
                const map = {};
                res.data.list.forEach((p) => {
                    map[p.name] = (p.fields || []).map((f) => f.name);
                });
                setPlatformFieldMap(map);
            }
        };
        fetchPlatforms();
    }, []);

    useEffect(() => {
        if (watchPlatform) {
            const fetchModels = async () => {
                const res = await getModelList({ platform: watchPlatform });
                if (res.code === 0) {
                    setModelOptions(
                        (res.data.list || [])
                            .filter((m) => m.status === '启用')
                            .map((m) => ({ value: m.name, label: m.name, mac_step: m.mac_step ?? 1 }))
                    );
                }
            };
            fetchModels();
            form.setFieldsValue({ model: undefined });
        } else {
            setModelOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchPlatform]);

    const uploadProps = {
        fileList,
        beforeUpload: (file) => {
            setFileList([file]);
            return false;
        },
        onRemove: () => setFileList([]),
        maxCount: 1,
        accept: '.xlsx,.xls',
    };

    const handleDownloadTemplate = () => {
        const cols = ['SN', ...(platformFieldMap[watchPlatform] || [])].map((name) => ({ label: name }));
        downloadCsv(`${watchPlatform || '平台'}_串码导入模板.csv`, cols, []);
    };

    const handleImport = async () => {
        const values = await form.validateFields();
        if (!fileList.length) {
            message.warning('请先上传 Excel 文件');
            return;
        }
        setImporting(true);
        try {
            const res = await importSerial({
                platform: values.platform,
                model: values.model,
                file_name: fileList[0].name,
            });
            if (res.code === 0) {
                const macStep = modelOptions.find((m) => m.value === values.model)?.mac_step ?? 1;
                message.success('导入成功');
                setResult({ success: true, total: 1000, macGenerated: 1000 * macStep, fail: 0 });
            } else {
                message.error(res.message || '导入失败');
                setResult({ success: false });
            }
        } catch (error) {
            console.error('Import failed:', error);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div style={{ padding: '16px' }}>
            <Card title="串码导入" style={{ maxWidth: 760 }}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="系统根据「平台」自动解析 SN、C*EI(CTEI/CMEI/CUEI)、UID、KEY 等字段，并按所选「设备型号」的生成MAC步长为每条串码分配对应数量的唯一 MAC 地址"
                />

                <Form form={form} layout="vertical">
                    <Form.Item name="platform" label="运营平台" rules={[{ required: true, message: '请选择运营平台' }]}>
                        <Select placeholder="请选择运营平台" options={PLATFORM_OPTIONS} style={{ width: 240 }} />
                    </Form.Item>

                    <Form.Item name="model" label="设备型号" rules={[{ required: true, message: '请选择设备型号' }]}>
                        <Select
                            placeholder="请先选择运营平台"
                            options={modelOptions}
                            disabled={!watchPlatform}
                            style={{ width: 240 }}
                        />
                    </Form.Item>

                    {watchModel && selectedMacStep != null && (
                        <div style={{ marginTop: -12, marginBottom: 16, color: 'rgba(0, 0, 0, 0.65)' }}>
                            生成MAC步长：<Tag color="blue">{selectedMacStep}</Tag>
                            每条串码将分配 <b>{selectedMacStep}</b> 个 MAC
                        </div>
                    )}

                    <Form.Item label="串码文件">
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                            <p className="ant-upload-text">点击或拖拽 Excel 文件到此处上传</p>
                            <p className="ant-upload-hint">支持 .xlsx / .xls，单个文件</p>
                        </Dragger>
                    </Form.Item>

                    {watchPlatform && (
                        <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 16 }}>
                            <Button type="link" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>下载模板</Button>
                            <div style={{ color: '#999', fontSize: 12 }}>
                                模板列：SN{platformFieldMap[watchPlatform]?.length ? `、${platformFieldMap[watchPlatform].join('、')}` : ''}
                            </div>
                        </div>
                    )}

                    <Form.Item>
                        <Space>
                            <Button type="primary" icon={<UploadOutlined />} loading={importing} onClick={handleImport}>
                                开始导入
                            </Button>
                            <Button onClick={() => { setFileList([]); setResult(null); form.resetFields(); }}>清空</Button>
                        </Space>
                    </Form.Item>
                </Form>

                {result && result.success && (
                    <Alert
                        type="success"
                        showIcon
                        message={`导入完成：共 ${result.total} 条，自动分配 MAC ${result.macGenerated} 条，失败 ${result.fail} 条`}
                    />
                )}
            </Card>
        </div>
    );
};

export default SerialImport;
